import type {
    AnalyticsChurn,
    AnalyticsRevenue,
    AnalyticsSubscriptions,
    SubscriptionCardId,
} from '../../services/analytics';
import { DASH, formatBRL, formatDays, formatInt, formatPct } from './format';
import type { MetricInfoKey } from './metricInfo';
import { STATUS_FILTER_CAPTION } from './overviewCards';

export interface SubscriptionCardView {
    id: MetricInfoKey;
    title: string;
    value: string;
    caption: string;
    /** Card que filtra a tabela "Assinaturas por cliente" ("Ver contas na tabela"). */
    tableCard?: SubscriptionCardId;
    /** Card sem lista para mostrar: rodape "Sem dados suficientes para detalhar", como no prototipo. */
    noDetail?: boolean;
    highlight?: boolean;
}

const plural = (n: number, one: string, many: string) => `${formatInt(n)} ${n === 1 ? one : many}`;

/** "1 de 26 pagantes iniciais". Sem pagantes no inicio, nao ha taxa. */
export function churnBaseCaption(count: number, base: number, what = 'pagantes iniciais'): string {
    if (base <= 0) return 'Sem pagantes no início do período';
    return `${formatInt(count)} de ${formatInt(base)} ${what}`;
}

/** Assinaturas > Geral: situacao das contas na data final, como no prototipo. */
export function buildGeneralCards(cards: AnalyticsSubscriptions['cards']): SubscriptionCardView[] {
    const ativos = cards.assinantesAtivos;
    return [
        {
            id: 'assinantesAtivos',
            title: 'Assinantes ativos',
            value: formatInt(ativos.value),
            caption: ativos.pct === null ? 'Sem contas no recorte' : `${formatPct(ativos.pct)} das contas do recorte`,
            tableCard: 'ativos',
            highlight: true,
        },
        { id: 'gratuitas', title: 'Gratuitas', value: formatInt(cards.gratuitas.value), caption: 'Acesso sem cobrança', tableCard: 'gratuitas' },
        { id: 'mensais', title: 'Mensais', value: formatInt(cards.mensais.value), caption: 'Plano ativo', tableCard: 'mensais' },
        { id: 'anuais', title: 'Anuais', value: formatInt(cards.anuais.value), caption: 'Plano ativo', tableCard: 'anuais' },
        {
            id: 'emAtraso',
            title: 'Em atraso',
            value: cards.emAtraso.value === null ? DASH : plural(cards.emAtraso.value, 'conta', 'contas'),
            caption: `${formatBRL(cards.emAtraso.mrr)}/mês de MRR exposto ao atraso`,
            tableCard: 'em_atraso',
        },
    ];
}

/** Assinaturas > Receitas. MRR, ARR e ARPU vem da base pagante: o link abre as pagantes. */
export function buildRevenueCards(cards: AnalyticsRevenue['cards']): SubscriptionCardView[] {
    const ltv = cards.ltvMedioPago;
    return [
        { id: 'mrr', title: 'MRR', value: formatBRL(cards.mrr.value), caption: 'Receita recorrente mensalizada', tableCard: 'ativos', highlight: true },
        { id: 'arr', title: 'ARR', value: formatBRL(cards.arr.value), caption: 'MRR × 12', tableCard: 'ativos' },
        { id: 'arpuMensal', title: 'ARPU mensal', value: formatBRL(cards.arpuMensal.value), caption: 'MRR ÷ assinantes ativos', tableCard: 'ativos' },
        {
            id: 'recebidoNoPeriodo',
            title: 'Recebido no período',
            value: formatBRL(cards.recebidoNoPeriodo.value),
            caption: 'Pagamentos confirmados no intervalo',
            tableCard: 'recebido',
        },
        {
            id: 'ltvMedioPago',
            title: 'LTV médio pago',
            value: formatBRL(ltv.value),
            caption: ltv.contas > 0 ? plural(ltv.contas, 'conta com pagamento', 'contas com pagamento') : 'Nenhum pagamento confirmado',
            tableCard: 'com_pagamento',
        },
    ];
}

/** Assinaturas > Churn. Expiracao conta como cancelamento; com filtro de status, as taxas ficam indisponiveis. */
export function buildChurnCards(cards: AnalyticsChurn['cards'], filtroDeStatus = false): SubscriptionCardView[] {
    const periodo = cards.churnNoPeriodo;
    const mensal = cards.churnMensal;
    const taxa = (caption: string) => (filtroDeStatus ? STATUS_FILTER_CAPTION : caption);
    return [
        {
            id: 'cancelamentosAssinaturas',
            title: 'Cancelamentos',
            value: formatInt(cards.cancelamentos.value),
            caption: 'Canceladas ou expiradas no período',
            tableCard: 'canceladas',
            highlight: true,
        },
        {
            id: 'churnNoPeriodo',
            title: 'Churn no período',
            value: formatPct(periodo.value),
            caption: taxa(churnBaseCaption(periodo.count, periodo.base)),
            tableCard: 'canceladas',
        },
        {
            id: 'churnMensal',
            title: 'Churn mensal',
            value: formatPct(mensal.value),
            caption: taxa(churnBaseCaption(mensal.count, mensal.base, 'pagantes no início do mês')),
            tableCard: 'canceladas_mes',
        },
        {
            id: 'churnAnual',
            title: 'Churn anual',
            value: formatPct(cards.churnAnual.value),
            caption: taxa(cards.churnAnual.value === null ? 'Histórico inferior a 12 meses' : 'Últimos 12 meses completos'),
            noDetail: true,
        },
        {
            id: 'mrrCanceladoAssinaturas',
            title: 'MRR cancelado',
            value: formatBRL(cards.mrrCancelado.value),
            caption: 'Valor mensalizado perdido',
            tableCard: 'canceladas',
        },
        {
            id: 'churnDeMrr',
            title: 'Churn de MRR',
            value: formatPct(cards.churnDeMrr.value),
            caption: taxa('Sobre o MRR inicial'),
            tableCard: 'canceladas',
        },
        {
            id: 'receitaPerdidaReal',
            title: 'Receita perdida real',
            value: formatBRL(cards.receitaPerdidaReal.value),
            caption: 'Requer conciliação',
            noDetail: true,
        },
        {
            id: 'trialAtePrimeiroPagamento',
            title: 'Trial → 1º pagamento',
            value: formatDays(cards.trialAtePrimeiroPagamentoDias.value),
            caption: cards.trialAtePrimeiroPagamentoDias.value === null ? 'Nenhuma gratuita virou pagante' : 'Tempo médio',
            tableCard: 'convertidas',
        },
        {
            id: 'ateCancelamento',
            title: 'Até cancelamento',
            value: formatDays(cards.ateCancelamentoDias.value),
            caption: cards.ateCancelamentoDias.value === null ? 'Sem cancelamento no período' : 'Tempo médio',
            tableCard: 'canceladas',
        },
    ];
}

/** Composicao atual: rotulo, contagem e percentual de cada situacao, na ordem do prototipo. */
export function buildComposition(c: AnalyticsSubscriptions['composicao']) {
    return [
        { id: 'ativas', label: 'Ativas', ...c.ativas },
        { id: 'emAtraso', label: 'Em atraso', ...c.emAtraso },
        { id: 'gratuitas', label: 'Gratuitas', ...c.gratuitas },
        { id: 'outras', label: 'Outras', ...c.outras },
    ].map((x) => ({ ...x, text: `${formatInt(x.count)} · ${formatPct(x.pct)}` }));
}

const CARD_LABELS: Record<SubscriptionCardId, string> = {
    ativos: 'Assinantes ativos',
    gratuitas: 'Gratuitas',
    mensais: 'Mensais',
    anuais: 'Anuais',
    em_atraso: 'Em atraso',
    recebido: 'Pagaram no período',
    com_pagamento: 'Com pagamento',
    canceladas: 'Canceladas no período',
    canceladas_mes: 'Canceladas no mês',
    convertidas: 'Gratuitas que viraram pagantes',
};

export const subscriptionCardLabel = (card: SubscriptionCardId): string => CARD_LABELS[card];
